import React, { ReactNode, FC, PropsWithChildren } from 'react';

import kebabCase from 'lodash/kebabCase';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import ReactPlayer, { ReactPlayerProps } from 'react-player/lazy';
import { sendBeaconEvent } from '@lucid/utils';

import VideoPlayButton from '../video-play-button/video-play-button';
import Box from '../../box/box';
import AccessibleDescription from '../../accessible-description/accessible-description';
import type { Beacon, LucidTheme } from '../../../types/types';

export const VideoPlaceholder = styled.div({
  label: 'videoPlaceholder',
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  zIndex: 1,
  img: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
  },
});

export const VideoOverlay = styled.div({
  label: 'videoOverlay',
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  background: 'rgba(0, 0, 0, 0.35)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 2,
});

export interface VideoPlayerProps extends ReactPlayerProps {
  /** The beacon event config. */
  beaconEvent?: Beacon.Event | null;
  /** Allows the component to get additional styles. */
  className?: string;
  /** An accessible description for the video content */
  description?: string;
  /** The unique ID for the description element (must be a valid id attribute) */
  descriptionElementId?: string;
  /** Sets a placeholder image while the video loads. */
  placeholder?: () => ReactNode;
  /** The url of a video or song to play, Can be an `array` or `MediaStream` object */
  url?: string;
  /** Will automatically play the video once loaded. */
  shouldAutoPlay?: boolean;
  /** Loads the video to play. */
  onClick?: () => void;
  /** Sets the video ratio, with the default being `wide`. */
  ratio?: 'square' | 'wide';
  /** Sets the color of the VideoPlayButton icon. */
  color?: string;
}

const VideoPlayer: FC<PropsWithChildren<VideoPlayerProps>> = ({
  beaconEvent,
  className,
  color = 'primary',
  description = '',
  descriptionElementId = '',
  url,
  placeholder,
  shouldAutoPlay,
  onClick = () => {
    return;
  },
  ratio = 'wide',
  ...rest
}) => {
  // url manipulations to match noembed regex patterns
  if (url) {
    url = url.replace('youtube.com/v/', 'youtube.com/watch?v=');
    if (url.startsWith('//')) {
      url = `https:${url}`;
    }
    if (url?.includes('autoplay=1')) {
      shouldAutoPlay = true;
      url = url?.replace('autoplay=1;rel=0', '');
      url = url?.replace('autoplay=1', '');
    }
  }

  const reactPlayerCss = css({
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
  });

  const videoContainerCss = (theme: LucidTheme) =>
    css({
      label: 'videoContainer',
      background: theme.colors.coolGrey[100],
      display: 'block',
      padding: 0,
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '5px',
      paddingTop: ratio === 'wide' ? '56.25%' : '100%',
    });

  const descriptionId = description ? kebabCase(descriptionElementId) : undefined;

  const handlePlayVideo = () => {
    if (beaconEvent) {
      sendBeaconEvent(beaconEvent);
    }
    onClick();
  };

  const playerOverlay = (
    <>
      <VideoOverlay
        onClick={handlePlayVideo}
        onKeyPress={e => {
          if (e.key === 'Enter' || e.key === 'Space') {
            handlePlayVideo();
          }
        }}
      >
        <VideoPlayButton color={color} variant="large" />
      </VideoOverlay>
      {placeholder && <VideoPlaceholder>{placeholder()}</VideoPlaceholder>}
    </>
  );

  return (
    <Box
      aria-describedby={descriptionId}
      as="div"
      css={videoContainerCss}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
      tabIndex={description ? 0 : -1}
    >
      {url ? (
        <ReactPlayer
          {...rest}
          controls
          playing
          config={{ wistia: { options: { controlsVisibleOnLoad: false, playButton: false } } }}
          css={reactPlayerCss}
          height="100%"
          light={!shouldAutoPlay}
          oEmbedUrl={
            url.indexOf('wistia') > -1
              ? 'https://fast.wistia.com/oembed?url={url}'
              : 'https://noembed.com/embed?url={url}'
          }
          playIcon={playerOverlay}
          url={url.replace(/\s+/g, '')}
          width="100%"
        />
      ) : (
        // render just the overlay/placeholder for video modal toggles
        playerOverlay
      )}
      {url && description && <AccessibleDescription id={descriptionId} SRText={description} />}
    </Box>
  );
};

export { VideoPlayer };
export default VideoPlayer;
